import { create } from "zustand";
import type { Collaboration, Notification, Operator } from "../types/database";

interface NetworkingState {
  // Collaborations
  collaborations: Collaboration[];
  pendingRequests: Collaboration[];
  activeCollaborations: Collaboration[];
  
  // Notifications
  notifications: Notification[];
  unreadCount: number;
  
  // UI State
  selectedOperator: Operator | null;
  isRequestModalOpen: boolean;
  
  // Actions
  setCollaborations: (collaborations: Collaboration[]) => void;
  addCollaboration: (collaboration: Collaboration) => void;
  updateCollaboration: (id: string, updates: Partial<Collaboration>) => void;
  removeCollaboration: (id: string) => void;
  
  setNotifications: (notifications: Notification[]) => void;
  addNotification: (notification: Notification) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  
  setSelectedOperator: (operator: Operator | null) => void;
  setIsRequestModalOpen: (isOpen: boolean) => void;
  
  // Derived state helpers
  refreshDerivedState: () => void;
}

export const useNetworkingStore = create<NetworkingState>((set, get) => ({
  // Initial state
  collaborations: [],
  pendingRequests: [],
  activeCollaborations: [],
  notifications: [],
  unreadCount: 0,
  selectedOperator: null,
  isRequestModalOpen: false,
  
  // Collaborations actions
  setCollaborations: (collaborations) => {
    set({ collaborations });
    get().refreshDerivedState();
  },
  
  addCollaboration: (collaboration) => {
    set((state) => ({ 
      collaborations: [collaboration, ...state.collaborations] 
    }));
    get().refreshDerivedState();
  },
  
  updateCollaboration: (id, updates) => {
    set((state) => ({
      collaborations: state.collaborations.map((c) =>
        c.id === id ? { ...c, ...updates } : c
      ),
    }));
    get().refreshDerivedState();
  },
  
  removeCollaboration: (id) => {
    set((state) => ({
      collaborations: state.collaborations.filter((c) => c.id !== id),
    }));
    get().refreshDerivedState();
  },
  
  // Notifications actions
  setNotifications: (notifications) => {
    set({ 
      notifications,
      unreadCount: notifications.filter((n) => !n.is_read).length,
    });
  },
  
  addNotification: (notification) => {
    set((state) => ({
      notifications: [notification, ...state.notifications],
      unreadCount: state.unreadCount + (notification.is_read ? 0 : 1),
    }));
  },
  
  markAsRead: (id) => {
    set((state) => ({
      notifications: state.notifications.map((n) =>
        n.id === id ? { ...n, is_read: true } : n
      ),
      unreadCount: Math.max(0, state.unreadCount - 1),
    }));
  },
  
  markAllAsRead: () => {
    set((state) => ({
      notifications: state.notifications.map((n) => ({ ...n, is_read: true })),
      unreadCount: 0,
    }));
  },
  
  // UI actions
  setSelectedOperator: (operator) => set({ selectedOperator: operator }),
  setIsRequestModalOpen: (isOpen) => set({ isRequestModalOpen: isOpen }),
  
  // Derived state
  refreshDerivedState: () => {
    const { collaborations } = get();
    set({
      pendingRequests: collaborations.filter((c) => c.status === "pending"),
      activeCollaborations: collaborations.filter((c) => c.status === "accepted"),
    });
  },
}));

